import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

// Daily Installations, rebuilt from the Power BI page of the same name.
//
// mode 'sharepoint' counts exactly as the Power BI report does, using only the
// mirrored SharePoint fields, so the two can be put side by side and proved
// equal. mode 'full' adds what NEO2 holds and Power BI cannot see.
const qs = (filters = {}, extra = {}) => {
  const p = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') p.set(k, v);
  });
  return p.toString();
};

export function useDailyInstallations(filters, mode) {
  return useQuery({
    queryKey: ['ops', 'daily', filters, mode],
    queryFn: async () => (await api.get(`/api/ops/daily-installations?${qs(filters, { mode })}`)).data,
    staleTime: 60000,
  });
}

export function useDailyInstallationJobs(filters, mode, { limit = 100, offset = 0 } = {}) {
  return useQuery({
    queryKey: ['ops', 'daily', 'jobs', filters, mode, limit, offset],
    queryFn: async () => (await api.get(
      `/api/ops/daily-installations/jobs?${qs(filters, { mode, limit, offset })}`)).data,
    staleTime: 60000,
  });
}

export function useDailyInstallationFilters() {
  return useQuery({
    queryKey: ['ops', 'daily', 'filters'],
    queryFn: async () => (await api.get('/api/ops/daily-installations/filters')).data,
    staleTime: 600000,
  });
}

// Installations Leaderboard. The ranking is a weighted score, so the response
// also carries the coverage the weighting could be applied to.
export function useLeaderboard(filters) {
  return useQuery({
    queryKey: ['ops', 'leaderboard', filters],
    queryFn: async () => (await api.get(`/api/ops/leaderboard?${qs(filters)}`)).data,
    staleTime: 60000,
  });
}

// Target v Performance. The response carries the figures and the caveats
// together on purpose - this is the page incentive payments come off, so the
// screen shows what the calculation could not be applied to as well as what it
// produced.
export function useTargetPerformance(filters) {
  return useQuery({
    queryKey: ['ops', 'target-performance', filters],
    queryFn: async () => (await api.get(`/api/ops/target-performance?${qs(filters)}`)).data,
    staleTime: 60000,
  });
}

// The six remaining rebuilt pages. They take the same month/region/contract
// filters and each returns its figures together with what the calculation
// could not be applied to, so one hook serves all of them.
export function useOpsPage(page, filters) {
  return useQuery({
    queryKey: ['ops', page, filters],
    queryFn: async () => (await api.get(`/api/ops/${page}?${qs(filters)}`)).data,
    staleTime: 60000,
    enabled: Boolean(page),
  });
}

// The reporting hub: one headline per page, from the pages' own services.
export function useOpsOverview(filters) {
  return useQuery({
    queryKey: ['ops', 'overview', filters],
    queryFn: async () => (await api.get(`/api/ops/overview?${qs(filters)}`)).data,
    staleTime: 60000,
  });
}

// Stock-logging KPIs: completed work only, to the end of yesterday.
export function useStockKpi(filters) {
  return useQuery({
    queryKey: ['ops', 'stock-kpi', filters],
    queryFn: async () => (await api.get(`/api/ops/stock-kpi?${qs(filters)}`)).data,
    staleTime: 60000,
  });
}

// One engineer's work orders behind their percentage. Only fetched when a row
// is opened, so the table loads without a request per engineer.
export function useStockKpiEngineer(email, filters) {
  return useQuery({
    queryKey: ['ops', 'stock-kpi', 'engineer', email, filters],
    queryFn: async () => (await api.get(`/api/ops/stock-kpi/engineer?${qs(filters, { email })}`)).data,
    enabled: Boolean(email),
    staleTime: 60000,
  });
}

// Individual support requests for a date range, with the engineer dropdown's
// names. Kept separate from useOpsPage('support-requests'), which is monthly
// totals; this is the "was there one on this day" list beneath them.
export function useSupportRequestList(filters) {
  return useQuery({
    queryKey: ['ops', 'support-requests-list', filters],
    queryFn: async () => (await api.get(`/api/ops/support-requests/list?${qs(filters)}`)).data,
    staleTime: 60000,
    // null = the screen has an invalid range; don't ask.
    enabled: Boolean(filters),
    // v5: keep showing the last results while a new search loads.
    placeholderData: (prev) => prev,
  });
}

// Open MEWP / Supervisor support requests with no decision, last 7 days.
// Refreshed every minute - it is a "who is still waiting" list.
export function useOpenSupportRequests(contract) {
  return useQuery({
    queryKey: ['ops', 'support-requests-open', contract || ''],
    queryFn: async () => (await api.get(`/api/ops/support-requests/open?${qs({ contract })}`)).data,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

// Individual records for a date range / name / search - the list beneath the
// Internal Remediations and Whereabouts Master pages (/api/ops/<page>/list).
export function useOpsRecordList(page, filters) {
  return useQuery({
    queryKey: ['ops', 'record-list', page, filters],
    queryFn: async () => (await api.get(`/api/ops/${page}/list?${qs(filters)}`)).data,
    staleTime: 60000,
    enabled: Boolean(filters),
    placeholderData: (prev) => prev,
  });
}
